import { type ReactNode } from 'react';
import { ColumnDef } from '@/components';
import { Tooltip } from '@/components/ui/Tooltip';
import { formatDateWithTime } from '@/utils/transformer';
import { Info } from 'lucide-react';

const USAGE_MONTH_LABELS = [
	'Jan',
	'Fev',
	'Mar',
	'Abr',
	'Mai',
	'Jun',
	'Jul',
	'Ago',
	'Set',
	'Out',
	'Nov',
	'Dez',
] as const;

const formatUsagePeriod = (year: number, month: number) => {
	const label = USAGE_MONTH_LABELS[month - 1];
	return label ? `${label}/${year}` : `${month}/${year}`;
};

const formatMetric = (value: number) => value.toLocaleString('pt-BR');

const ColumnHeaderWithHint = ({
	label,
	hint,
}: {
	label: string;
	hint: string;
}) => (
	<span className="inline-flex items-center justify-center gap-0.5">
		<span>{label}</span>
		<Tooltip content={hint} position="top">
			<button
				type="button"
				className="inline-flex shrink-0 cursor-help rounded-full text-stone-400 transition-colors hover:text-stone-600"
				aria-label={hint}
			>
				<Info
					className="size-3 shrink-0"
					strokeWidth={2.25}
					aria-hidden
				/>
			</button>
		</Tooltip>
	</span>
);

const MetricCell = ({ children }: { children: ReactNode }) => (
	<span className="block truncate text-right text-xs tabular-nums text-dark">
		{children}
	</span>
);

const ProfessionalMonthlyUsageColumns = (): ColumnDef<
	Professionals.MonthlyUsageItem,
	keyof Professionals.MonthlyUsageItem
>[] => [
	{
		header: 'Período',
		accessor: 'usage_year',
		cell: (_, row) => (
			<span className="text-xs font-semibold text-dark">
				{formatUsagePeriod(row.usage_year, row.usage_month)}
			</span>
		),
	},
	{
		header: (
			<ColumnHeaderWithHint
				label="Claude (entrada)"
				hint="Tokens enviados ao Claude (contexto + prompt)"
			/>
		),
		accessor: 'claude_input_tokens',
		headerClassName: 'text-right',
		cellClassName: 'text-right',
		cell: (value) => <MetricCell>{formatMetric(Number(value))}</MetricCell>,
	},
	{
		header: (
			<ColumnHeaderWithHint
				label="Claude (saída)"
				hint="Tokens gerados pelo Claude"
			/>
		),
		accessor: 'claude_output_tokens',
		headerClassName: 'text-right',
		cellClassName: 'text-right',
		cell: (value) => <MetricCell>{formatMetric(Number(value))}</MetricCell>,
	},
	{
		header: (
			<ColumnHeaderWithHint
				label="Chamadas IA"
				hint="Quantidade de chamadas à API (1 por sessão com IA processada)"
			/>
		),
		accessor: 'claude_api_calls',
		headerClassName: 'text-right',
		cellClassName: 'text-right',
		cell: (value) => <MetricCell>{formatMetric(Number(value))}</MetricCell>,
	},
	{
		header: (
			<ColumnHeaderWithHint
				label="Vídeo (min)"
				hint="Soma dos minutos de participantes na sala (P2P online)"
			/>
		),
		accessor: 'daily_co_call_minutes',
		headerClassName: 'text-right',
		cellClassName: 'text-right',
		cell: (value) => <MetricCell>{formatMetric(Number(value))}</MetricCell>,
	},
	{
		header: (
			<ColumnHeaderWithHint
				label="Salas"
				hint="Quantidade de sessões de vídeo finalizadas contabilizadas"
			/>
		),
		accessor: 'daily_co_room_sessions',
		headerClassName: 'text-right',
		cellClassName: 'text-right',
		cell: (value) => <MetricCell>{formatMetric(Number(value))}</MetricCell>,
	},
	{
		header: (
			<ColumnHeaderWithHint
				label="Transcr. (min)"
				hint="Minutos de transcrição (quando transcription_started na sessão)"
			/>
		),
		accessor: 'daily_co_transcription_minutes',
		headerClassName: 'text-right',
		cellClassName: 'text-right',
		cell: (value) => <MetricCell>{formatMetric(Number(value))}</MetricCell>,
	},
	{
		header: 'Atualizado em',
		accessor: 'updated_at',
		cell: (value) => (
			<span className="block truncate text-xs text-stone-500">
				{formatDateWithTime(value as string)}
			</span>
		),
	},
];

export { ProfessionalMonthlyUsageColumns };
